import React from "react";
import { get, sum } from "lodash";
import { PaymentMethod } from "../../../../common/enums";
import { LPSelectAvailablePaymentPrefsHeader } from "../../Components/LPSelectAvailablePaymentPrefsHeader";
import { type PaymentMethodValue, type AccountEdge } from "../../Context/types";
import { PAYMENT_METHOD_NOT_SUPPORTED, PAYMENT_METHOD_PARTIALLY_SUPPORTED } from "./strings";

/**
 * Get the total number of positions across all accounts.
 * @param accounts The list of investment accounts.
 * @returns The total number of positions across all accounts.
 */
export const getTotalPositionsCount = (accounts: AccountEdge[]) =>
  sum(
    accounts.map((account) => {
      return account?.node?.positions?.count;
    }),
  );

/*
 * Get the warning message based on the supported positions count and total positions count.
 * @param supportedPositionsCount The number of supported positions.
 * @param totalPositionsCount The total number of positions.
 * @returns The warning message.
 */
export const getWarningMessage = ({
  supportedPositionsCount,
  totalPositionsCount,
}: {
  supportedPositionsCount: number;
  totalPositionsCount: number;
}) => {
  if (supportedPositionsCount === 0) {
    return PAYMENT_METHOD_NOT_SUPPORTED;
  } else if (totalPositionsCount !== supportedPositionsCount) {
    return PAYMENT_METHOD_PARTIALLY_SUPPORTED;
  }

  return "";
};

/**
 * Get the number of positions that support the payment method.
 * @param accounts The list of investment accounts.
 * @param paymentMethod The payment method.
 * @returns The number of positions that support the payment method.
 */
export const getSupportedPositionsCountForPaymentMethod = ({
  accounts,
  paymentMethod,
}: {
  accounts: AccountEdge[];
  paymentMethod: PaymentMethod;
}) =>
  sum(
    accounts.map((account) => {
      return get(account?.node?.paymentMethodSupportedCounts, paymentMethod.toLowerCase(), 0);
    }),
  );

/**
 * Convert the supported positions count and total positions count to a PaymentMethodValue object.
 * @param supportedCount The number of supported positions.
 * @param totalCount The total number of positions.
 * @returns A PaymentMethodValue object that contains the availability and warning message.
 */
export const getPaymentMethodValue = ({
  supportedPositionsCount,
  totalPositionsCount,
}: {
  supportedPositionsCount: number;
  totalPositionsCount: number;
}): PaymentMethodValue => ({
  isAvailable: supportedPositionsCount > 0,
  warning: getWarningMessage({
    supportedPositionsCount,
    totalPositionsCount,
  }),
});

/*
 * Get the PaymentMethodValue for the given payment method.
 * @param paymentMethod The payment method.
 * @param accounts The list of investment accounts.
 * @param totalCount The total number of positions.
 * @returns The PaymentMethodValue for the given payment method.
 */
export const getPaymentMethodValueForPaymentMethod = ({
  paymentMethod,
  accounts,
  totalPositionsCount,
}: {
  paymentMethod: PaymentMethod;
  accounts: AccountEdge[];
  totalPositionsCount: number;
}): PaymentMethodValue => {
  const supportedPositionsCount = getSupportedPositionsCountForPaymentMethod({
    accounts,
    paymentMethod,
  });
  return getPaymentMethodValue({ supportedPositionsCount, totalPositionsCount });
};

/*
 * Get the available payment methods for the given accounts.
 * @param accounts The list of investment accounts.
 * @returns The available payment methods.
 */
export const getAvailablePaymentMethods = (
  accounts: AccountEdge[],
): Record<PaymentMethod, PaymentMethodValue> => {
  const totalPositionsCount = getTotalPositionsCount(accounts);
  return Object.values(PaymentMethod).reduce<Record<PaymentMethod, PaymentMethodValue>>(
    (acc, paymentMethod) => {
      return {
        ...acc,
        [paymentMethod]: getPaymentMethodValueForPaymentMethod({
          paymentMethod,
          accounts,
          totalPositionsCount,
        }),
      };
    },
    {
      [PaymentMethod.ACH]: {
        isAvailable: true,
        warning: "",
      },
      [PaymentMethod.Wire]: {
        isAvailable: true,
        warning: "",
      },
      [PaymentMethod.Check]: {
        isAvailable: true,
        warning: "",
      },
    },
  );
};

export const createLpHeaderComponent =
  (accounts?: AccountEdge[]): React.FC =>
  // eslint-disable-next-line react/display-name
  () => {
    if (!accounts) {
      return null;
    }
    const allInvestmentEntities: string[] = accounts
      .flatMap((account) => {
        return (
          account?.node?.positions?.edges.map((x) => x?.node?.investmentEntity?.name ?? "") ??
          []
        );
      })
      .filter((investmentEntity) => !!investmentEntity);

    return <LPSelectAvailablePaymentPrefsHeader count={allInvestmentEntities.length} />;
  };
